/**
 * Product Comparison Grid
 *
 * Handles:
 *   1. The "show more rows" toggle (height-animated).
 *   2. The feature info popups — hover on desktop, tap on mobile.
 *
 * Self-registering custom element, no dependencies, no jQuery.
 */
if (!customElements.get('product-comparison-grid')) {
  class ProductComparisonGrid extends HTMLElement {
    constructor() {
      super();
      this.grid = this.querySelector('.product-comparison-grid');
      this.rows = this.grid ? this.grid.querySelectorAll('tr') : [];
      this.gridContainer = this.querySelector('.pcg-container');
      this.showAllButton = this.querySelector('.js-show-all');
      this.activePopup = null;
    }

    connectedCallback() {
      if (this.showAllButton) {
        this.showAllButton.addEventListener('click', this.handleShowAllClick.bind(this));
      }
      this.initPopups();
    }

    /* ----------------------------------------------------------------------
       Show / hide additional rows
       ---------------------------------------------------------------------- */

    handleShowAllClick(evt) {
      const button = evt.currentTarget;

      // Toggle the button text
      const buttonText = button.textContent.trim();
      button.textContent = button.dataset.toggleText;
      button.dataset.toggleText = buttonText;

      // Set the initial max height to transition from
      const gridHeight = this.grid.clientHeight;
      this.gridContainer.style.maxHeight = `${gridHeight}px`;
      this.gridContainer.style.overflow = 'hidden';
      this.gridContainer.classList.add('is-transitioning');

      // Show/hide the relevant rows
      let heightReduction = 0;
      this.rows.forEach((row) => {
        if (row.classList.contains('js-hidden')) {
          row.classList.remove('js-hidden');
          row.dataset.wasHidden = 'true';
        } else if (row.dataset.wasHidden) {
          heightReduction += row.clientHeight;
        }
      });

      this.gridContainer.style.maxHeight =
        `${heightReduction > 0 ? gridHeight - heightReduction : this.grid.clientHeight}px`;

      const duration =
        parseFloat(window.getComputedStyle(this.gridContainer).transitionDuration) * 1000;

      setTimeout(() => {
        if (heightReduction > 0) {
          this.rows.forEach((row) => {
            if (row.dataset.wasHidden === 'true') {
              row.classList.add('js-hidden');
              delete row.dataset.wasHidden;
            }
          });
        }

        // Reset the height back to auto
        this.gridContainer.style.maxHeight = '';
        this.gridContainer.style.overflow = '';
        this.gridContainer.classList.remove('is-transitioning');
      }, duration);
    }

    /* ----------------------------------------------------------------------
       Feature info popups
       ---------------------------------------------------------------------- */

    initPopups() {
      // Desktop: hover to reveal.
      // The popup is offset from the icon, so there is a gap the cursor must
      // cross. Closing is deferred and cancelled if the pointer lands on
      // either the icon or the popup, which keeps the panel reachable.
      this.querySelectorAll('.pcg-info-icon').forEach((icon) => {
        const popup = this.querySelector(`#${icon.dataset.popup}`);
        if (!popup) return;

        let closeTimer = null;
        const cancelClose = () => {
          clearTimeout(closeTimer);
          closeTimer = null;
        };
        const scheduleClose = () => {
          cancelClose();
          closeTimer = setTimeout(() => this.closePopup(popup), 220);
        };

        icon.addEventListener('mouseenter', () => {
          cancelClose();
          this.openPopup(popup);
        });
        icon.addEventListener('mouseleave', scheduleClose);

        popup.addEventListener('mouseenter', cancelClose);
        popup.addEventListener('mouseleave', scheduleClose);

        // Keyboard parity
        icon.addEventListener('focus', () => {
          cancelClose();
          this.openPopup(popup);
        });
        icon.addEventListener('blur', scheduleClose);
        popup.addEventListener('focusin', cancelClose);
        popup.addEventListener('focusout', scheduleClose);
      });

      // Mobile: tap to reveal, tap close button to dismiss
      this.querySelectorAll('.pcg-mobile-info-icon').forEach((icon) => {
        const popup = this.querySelector(`#${icon.dataset.popup}`);
        if (!popup) return;

        icon.addEventListener('click', (evt) => {
          evt.stopPropagation();
          if (popup.classList.contains('is-open')) {
            this.closePopup(popup);
          } else {
            this.openPopup(popup);
          }
        });

        const close = popup.querySelector('.pcg-popup__close');
        if (close) {
          close.addEventListener('click', (evt) => {
            evt.stopPropagation();
            this.closePopup(popup);
          });
        }
      });

      // Dismiss on outside click / Escape
      this.outsideClickHandler = (evt) => {
        if (this.activePopup && !this.activePopup.contains(evt.target)) {
          this.closePopup(this.activePopup);
        }
      };
      this.escapeHandler = (evt) => {
        if (evt.key === 'Escape' && this.activePopup) this.closePopup(this.activePopup);
      };

      document.addEventListener('click', this.outsideClickHandler);
      document.addEventListener('keydown', this.escapeHandler);
    }

    openPopup(popup) {
      // Already open — do nothing, or re-entering would restart a playing video
      if (popup.classList.contains('is-open')) {
        this.activePopup = popup;
        return;
      }

      if (this.activePopup && this.activePopup !== popup) this.closePopup(this.activePopup);

      popup.classList.add('is-open');

      // Lift the row above its siblings so the popup is not clipped
      const row = popup.closest('.pcg-metafield-row-mobile');
      if (row) row.classList.add('active');

      this.activePopup = popup;
    }

    closePopup(popup) {
      popup.classList.remove('is-open');

      const row = popup.closest('.pcg-metafield-row-mobile');
      if (row) row.classList.remove('active');

      const video = popup.querySelector('video');
      if (video && !video.paused) video.pause();

      if (this.activePopup === popup) this.activePopup = null;
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.outsideClickHandler);
      document.removeEventListener('keydown', this.escapeHandler);
    }
  }

  customElements.define('product-comparison-grid', ProductComparisonGrid);
}
